import { useFontStore } from '@/store_pinia/fontStore';
import { backgroundColorToCss, BLOCK_TYPES, DESIGN_B } from 'src/helpers/builderBlocksUtils';
import {
  GruposReservaAlojamientos,
  GruposReservaFormulario,
  GruposReservaFormularioUnirse,
  GruposReservaFormularioUnirseSinAlojamiento,
  GruposReservaResumen,
  GruposPropuestaResumen,
  ViajesGrupoModificarReservas,
  GruposViajesThankYou,
} from '@/router/RouterNames';

const VIAJE_EXPERIENCIA_STYLE_ROUTES = new Set([
  GruposReservaAlojamientos,
  GruposReservaFormulario,
  GruposReservaFormularioUnirse,
  GruposReservaFormularioUnirseSinAlojamiento,
  GruposReservaResumen,
  GruposPropuestaResumen,
  ViajesGrupoModificarReservas,
  GruposViajesThankYou,
]);

export function isViajeExperienciaStyleRoute(routeName) {
  return VIAJE_EXPERIENCIA_STYLE_ROUTES.has(routeName);
}

const LANDING_SPACING = {
  XS: { desktop: '10px', mobile: '10px' },
  S: { desktop: '25px', mobile: '15px' },
  M: { desktop: '40px', mobile: '20px' },
  L: { desktop: '55px', mobile: '25px' },
  XL: { desktop: '70px', mobile: '30px' },
  XXL: { desktop: '85px', mobile: '40px' },
};

const SECTION_SPACING_CLASSES = {
  XS: ['py-10', 'py-m-10'],
  S: ['py-25', 'py-m-15'],
  M: ['py-40', 'py-m-20'],
  L: ['py-55', 'py-m-25'],
  XL: ['py-70', 'py-m-30'],
  XXL: ['py-85', 'py-m-40'],
};

function typographyToFontFamily(typography) {
  return typography?.nombre ? `${typography?.nombre}, sans-serif` : null;
}

export function loadLandingFonts(style) {
  useFontStore().loadLandingFonts(style?.typography);
}

function hexToRgbCsv(color) {
  if (!color) return null;
  const hex = String(color).replace('#', '');
  const rgb = hex.length === 8 ? hex.slice(0, 6) : hex;
  if (rgb.length !== 6 || !/^[0-9A-Fa-f]{6}$/.test(rgb)) return null;
  const r = parseInt(rgb.slice(0, 2), 16);
  const g = parseInt(rgb.slice(2, 4), 16);
  const b = parseInt(rgb.slice(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

function normalizeLandingStyle(style) {
  if (!style) return null;

  const { colors = {}, buttons = {}, typography = {}, spacing } = style;
  const spacingValues = LANDING_SPACING[spacing || 'M'];
  const textTypography = typographyToFontFamily(typography.textTypography);
  const titleTypography = typographyToFontFamily(typography.titleTypography);

  return {
    ...colors,
    ...buttons,
    titleTypography,
    textTypography,
    spacingDesktop: spacingValues.desktop,
    spacingMobile: spacingValues.mobile,
    textColorRgb: hexToRgbCsv(colors.textColor),
    detailColorRgb: hexToRgbCsv(colors.detailColor),
    backgroundColorRgb: hexToRgbCsv(colors.backgroundColor),
    secondaryButtonTextColorRgb: hexToRgbCsv(buttons.secondaryButtonTextColor),
  };
}

export function landingStyleVars(style) {
  const landingStyle = normalizeLandingStyle(style);
  if (!landingStyle) return null;

  loadLandingFonts(style);

  return {
    '--landing-background-color': landingStyle.backgroundColor,
    '--landing-background-color-rgb': landingStyle.backgroundColorRgb,
    '--landing-text-color': landingStyle.textColor,
    '--landing-text-color-rgb': landingStyle.textColorRgb,
    '--landing-title-color': landingStyle.titleColor,
    '--landing-detail-color': landingStyle.detailColor,
    '--landing-detail-color-rgb': landingStyle.detailColorRgb,
    '--landing-text-font': landingStyle.textTypography,
    '--landing-title-font': landingStyle.titleTypography,
    '--landing-spacing-desktop': landingStyle.spacingDesktop,
    '--landing-spacing-mobile': landingStyle.spacingMobile,
    '--landing-button-font': landingStyle.textTypography,
    '--landing-primary-button-background-color': landingStyle.primaryButtonBackgroundColor,
    '--landing-primary-button-text-color': landingStyle.primaryButtonTextColor,
    '--landing-primary-button-border': landingStyle.primaryButtonBorder,
    '--landing-primary-button-border-radius': landingStyle.primaryButtonBorderRadius,
    '--landing-primary-button-font-weight': landingStyle.primaryButtonFontWeight,
    '--landing-secondary-button-text-color': landingStyle.secondaryButtonTextColor,
    '--landing-secondary-button-text-color-rgb': landingStyle.secondaryButtonTextColorRgb,
    '--landing-secondary-button-border': landingStyle.secondaryButtonBorder,
    '--landing-secondary-button-border-radius': landingStyle.secondaryButtonBorderRadius,
    '--landing-secondary-button-font-weight': landingStyle.secondaryButtonFontWeight,
  };
}

export function landingModalStyleVars(style, block = null) {
  const base = landingStyleVars(style);
  if (!base) return null;

  const sectionStyle = block ? getSectionWrapperStyle(block, style) : null;
  const sectionBg = sectionStyle?.['--landing-section-background-color'];
  if (!sectionBg) return base;

  return {
    ...base,
    '--landing-background-color': sectionBg,
    '--landing-section-background-color': sectionBg,
    '--landing-section-background-color-rgb': sectionStyle['--landing-section-background-color-rgb'],
  };
}

export function landingHeaderVars(style) {
  if (!style?.colors?.backgroundColorHeader || !style?.colors?.textColorHeader) return null;
  return {
    '--landing-header-bg': style.colors.backgroundColorHeader,
    '--landing-header-color': style.colors.textColorHeader,
    '--landing-header-minimized-color': style.colors.textColorHeader,
    '--landing-header-detail-color': style.colors.detailColor,
    '--landing-text-font': typographyToFontFamily(style?.typography?.textTypography) || 'inherit',
  };
}

export function landingFooterVars(style) {
  if (!style?.colors?.backgroundColorFooter || !style?.colors?.textColorFooter) return null;
  return {
    '--landing-footer-bg': style.colors.backgroundColorFooter,
    '--landing-footer-color': style.colors.textColorFooter,
    '--landing-text-font': typographyToFontFamily(style?.typography?.textTypography) || 'inherit',
  };
}

export function getSectionSpacingClasses(spacing) {
  return SECTION_SPACING_CLASSES[spacing || 'M'];
}

export function getBlockSpacingClasses(block, spacing) {
  if (block?.tipo === BLOCK_TYPES.BANNER) {
    return null;
  }

  if (block?.tipo === BLOCK_TYPES.IMAGES && block?.design === DESIGN_B && !block?.nombre?.trim()) {
    return null;
  }

  if (block?.tipo === BLOCK_TYPES.COUNTDOWN && !block?.nombre?.trim()) {
    return null;
  }

  return getSectionSpacingClasses(spacing);
}

export function getSectionWrapperStyle(block, style) {
  const backgroundColor = backgroundColorToCss(block?.backgroundColor) || normalizeLandingStyle(style)?.backgroundColor;

  return {
    backgroundColor,
    '--landing-section-background-color': backgroundColor,
    '--landing-section-background-color-rgb': hexToRgbCsv(backgroundColor),
  };
}
